import React, { useEffect, useState } from "react";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { CalendarClock, Check, AlertTriangle } from "lucide-react";

// Public (no-auth) meeting self-signup page reached via magic link from email.
// Route: /meeting-signup/:token — wired in App.js as a public route.
export default function MeetingSignupPage() {
  const token = window.location.pathname.split("/").pop();
  const API = process.env.REACT_APP_BACKEND_URL;
  const [state, setState] = useState({ loading: true, error: null, meeting: null, done: false });
  const [picked, setPicked] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/public/meetings/signup/${token}`)
      .then(({ data }) => setState({ loading: false, error: null, meeting: data, done: false }))
      .catch((e) => setState({ loading: false, error: e?.response?.data?.detail || "Link is invalid or expired", meeting: null, done: false }));
  }, [token, API]);

  const submit = async () => {
    if (!picked) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/public/meetings/signup/${token}`, { session_id: picked });
      setState((s) => ({ ...s, done: true }));
    } catch (e) {
      setState((s) => ({ ...s, error: e?.response?.data?.detail || "Failed to submit" }));
    }
    setSubmitting(false);
  };

  if (state.loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  }
  if (state.error && !state.meeting) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <AlertTriangle size={40} className="mx-auto text-amber-500 mb-3" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Link problem</h1>
          <p className="text-sm text-slate-600">{state.error}</p>
        </div>
      </div>
    );
  }
  if (state.done) {
    const sess = (state.meeting.sessions || []).find(s => s.id === picked);
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <Check size={40} className="mx-auto text-emerald-500 mb-3" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">You're all set</h1>
          <p className="text-sm text-slate-600">
            You'll attend <b>{state.meeting.meeting_name}</b> on{" "}
            {sess && <b>{format(parseISO(sess.date), "EEEE, MMM d")} · {sess.start_time}–{sess.end_time}</b>}.
          </p>
          <p className="text-xs text-slate-400 mt-4">You can close this page.</p>
        </div>
      </div>
    );
  }
  const mtg = state.meeting;
  return (
    <div className="min-h-screen bg-slate-50 p-6 flex items-start justify-center">
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-lg p-8 mt-8">
        <div className="flex items-center gap-2 mb-3">
          <CalendarClock size={20} className="text-fuchsia-600" />
          <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Self signup</div>
        </div>
        <h1 className="text-2xl font-bold text-slate-900">{mtg.meeting_name}</h1>
        <p className="text-sm text-slate-600 mt-1">
          Hi {mtg.employee_name || "there"} — pick a session you can attend:
        </p>
        <div className="mt-4 space-y-2">
          {(mtg.sessions || []).map((s) => (
            <button
              key={s.id}
              onClick={() => setPicked(s.id)}
              data-testid={`pick-${s.id}`}
              className={`w-full text-left rounded-lg border p-3 transition-colors ${
                picked === s.id
                  ? "border-fuchsia-500 bg-fuchsia-50 ring-2 ring-fuchsia-200"
                  : "border-slate-200 hover:bg-slate-50"
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded" style={{ background: s.color }} />
                <div className="font-bold text-slate-900">
                  {format(parseISO(s.date), "EEEE, MMM d")}
                </div>
                <div className="text-sm text-slate-600">
                  {s.start_time}–{s.end_time}
                </div>
              </div>
            </button>
          ))}
        </div>
        {state.error && (
          <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
            {state.error}
          </div>
        )}
        <button
          onClick={submit}
          disabled={!picked || submitting}
          data-testid="signup-submit"
          className="mt-5 w-full rounded-lg bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-semibold py-3 disabled:opacity-50 transition-colors"
        >
          {submitting ? "Submitting…" : "Confirm my session"}
        </button>
      </div>
    </div>
  );
}
